import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReaderBlock } from '@bookclub/shared';
import { getMediaCaptionText, MediaCaption, normalizeRichTextHtml } from './MediaCaption';

type ReaderMediaState = 'not_started' | 'in_progress' | 'completed';
const ROUND_VIDEO_PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2] as const;
const ROUND_VIDEO_OVERLAY_AUTO_HIDE_MS = 1600;
export type RoundVideoPlaybackRate = (typeof ROUND_VIDEO_PLAYBACK_RATES)[number];

interface BlockRendererProps {
  block: ReaderBlock;
  defaultVideoPoster?: string | null;
  onVideoTimeUpdate?: (blockId: string, positionSeconds: number, durationSeconds: number) => void;
  onVideoEnded?: (blockId: string, positionSeconds: number, durationSeconds: number) => void;
  onMediaPlay?: (blockId: string) => void;
  onMediaPause?: (blockId: string) => void;
  registerMediaElement?: (blockId: string, element: HTMLMediaElement | null) => void;
  videoPosition?: number;
  mediaState?: ReaderMediaState;
  presentation?: 'default' | 'caption';
  roundVideoPlaybackRate?: RoundVideoPlaybackRate;
  onRoundVideoPlaybackRateChange?: (rate: RoundVideoPlaybackRate) => void;
}

export function BlockRenderer({
  block,
  defaultVideoPoster,
  onVideoTimeUpdate,
  onVideoEnded,
  onMediaPlay,
  onMediaPause,
  registerMediaElement,
  videoPosition,
  mediaState = 'not_started',
  presentation = 'default',
  roundVideoPlaybackRate,
  onRoundVideoPlaybackRateChange,
}: BlockRendererProps) {
  const p = block.payload;

  switch (block.type) {
    case 'text':
      return <TextBlock text={p['text'] as string} presentation={presentation} />;

    case 'heading':
      return <HeadingBlock text={(p['text'] as string) || ''} level={(p['level'] as number) || 2} />;

    case 'quote':
      return <QuoteBlock text={(p['text'] as string) || ''} author={(p['author'] as string) || undefined} />;

    case 'list':
      return <ListBlock items={(p['items'] as string[]) || []} ordered={(p['ordered'] as boolean) || false} />;

    case 'image':
      return (
        <ImageBlock
          url={block.mediaUrl || (p['url'] as string) || ''}
          alt={(p['alt'] as string) || ''}
          caption={getMediaCaptionText(block)}
        />
      );

    case 'video':
      {
        const payloadPoster = typeof p['posterUrl'] === 'string' && p['posterUrl'].trim().length > 0 ? (p['posterUrl'] as string) : undefined;
      return (
        <VideoBlock
          blockId={block.id}
          url={block.mediaUrl || ''}
          mimeType={block.mediaMimeType || undefined}
          poster={payloadPoster ?? defaultVideoPoster ?? undefined}
          style={(p['style'] as 'regular' | 'round') || 'regular'}
          initialPosition={videoPosition}
          mediaState={mediaState}
          registerMediaElement={registerMediaElement}
          onPlay={onMediaPlay}
          onPause={onMediaPause}
          onTimeUpdate={onVideoTimeUpdate}
          onEnded={onVideoEnded}
          playbackRate={roundVideoPlaybackRate}
          onPlaybackRateChange={onRoundVideoPlaybackRateChange}
        />
      );
      }

    case 'audio':
      return (
        <AudioBlock
          blockId={block.id}
          url={block.mediaUrl || (p['url'] as string) || ''}
          title={(p['title'] as string) || undefined}
          isTelegramVoice={isTelegramVoiceAudioPayload(p)}
          registerMediaElement={registerMediaElement}
          onPlay={onMediaPlay}
          onPause={onMediaPause}
          onTimeUpdate={onVideoTimeUpdate}
          onEnded={onVideoEnded}
        />
      );

    case 'divider':
      return <DividerBlock />;

    case 'callout':
      return <CalloutBlock text={p['text'] as string} emoji={p['emoji'] as string | undefined} />;

    case 'external_link':
      return (
        <ExternalLinkBlock
          url={(p['url'] as string) || ''}
          label={(p['label'] as string) || (p['url'] as string) || ''}
        />
      );

    default:
      return (
        <div className="mx-4 rounded-[20px] bg-[rgba(26,26,26,0.06)] px-4 py-3 text-sm text-[rgba(26,26,26,0.46)]">
          Unsupported block type: {block.type}
        </div>
      );
  }
}

function isTelegramVoiceAudioPayload(payload: ReaderBlock['payload']) {
  return payload['style'] === 'voice' || payload['telegramSourceType'] === 'voice';
}

// ─── Individual block components ─────────────────────────────────────────────

function TextBlock({ text, presentation }: { text: string; presentation: 'default' | 'caption' }) {
  const isCaption = presentation === 'caption';
  const normalizedText = normalizeRichTextHtml(text);

  if (!normalizedText) return null;

  return (
    <div
      className={[
        'reader-prose max-w-none',
        isCaption
          ? 'px-5 py-0.5 text-[13px] leading-[1.55] text-[rgba(26,26,26,0.56)] min-[430px]:px-6'
          : 'px-5 py-0.5 font-serif text-[16.5px] leading-[1.58] tracking-[-0.01em] text-[#2E2E2E] min-[430px]:px-6 min-[430px]:text-[17px]',
      ].join(' ')}
      // Admin uses plain textarea input for text blocks; normalize raw text into real paragraphs here.
      dangerouslySetInnerHTML={{ __html: normalizedText }}
    />
  );
}

function HeadingBlock({ text, level }: { text: string; level: number }) {
  const className = 'px-5 py-2.5 font-serif font-semibold tracking-[-0.02em] text-[var(--foreground)] min-[430px]:px-6';
  const sizes: Record<number, string> = {
    1: 'text-[28px] leading-[1.08]',
    2: 'text-[23px] leading-[1.12]',
    3: 'text-[19px] leading-[1.16]',
    4: 'text-[16px] leading-[1.2]',
  };
  return (
    <div className={`${className} ${sizes[level] ?? 'text-xl'}`}>
      {text}
    </div>
  );
}

function QuoteBlock({ text, author }: { text: string; author?: string }) {
  return (
    <blockquote className="mx-5 rounded-[22px] border-l-4 border-[var(--accent)] bg-[rgba(255,248,231,0.72)] px-5 py-3 min-[430px]:mx-6">
      <p className="font-serif text-[17px] italic leading-[1.82] text-[rgba(26,26,26,0.76)]">{text}</p>
      {author && <cite className="mt-2 block text-[12px] font-medium text-[rgba(26,26,26,0.44)] not-italic">— {author}</cite>}
    </blockquote>
  );
}

function ListBlock({ items, ordered }: { items: string[]; ordered: boolean }) {
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag className={`px-7 py-2.5 pl-10 space-y-2 ${ordered ? 'list-decimal' : 'list-disc'} min-[430px]:px-8 min-[430px]:pl-11`}>
      {items.map((item, i) => (
        <li key={i} className="font-serif text-[17px] leading-[1.82] text-[#2E2E2E]">
          {item}
        </li>
      ))}
    </Tag>
  );
}

function ImageBlock({ url, alt, caption }: { url: string; alt: string; caption?: string | null }) {
  if (!url) return null;
  return (
    <figure className="px-3 min-[430px]:px-4">
      <img
        src={url}
        alt={alt}
        className="w-full rounded-[22px] object-cover shadow-[0_14px_28px_rgba(62,48,23,0.11)]"
        loading="lazy"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
      <MediaCaption caption={caption} className="mt-2 px-2 min-[430px]:px-3" testId="image-caption" />
    </figure>
  );
}

interface VideoBlockProps {
  blockId: string;
  url: string;
  mimeType?: string;
  poster?: string;
  style: 'regular' | 'round';
  initialPosition?: number;
  mediaState: ReaderMediaState;
  registerMediaElement?: (blockId: string, element: HTMLMediaElement | null) => void;
  onPlay?: (blockId: string) => void;
  onPause?: (blockId: string) => void;
  onTimeUpdate?: (blockId: string, positionSeconds: number, durationSeconds: number) => void;
  onEnded?: (blockId: string, positionSeconds: number, durationSeconds: number) => void;
  playbackRate?: RoundVideoPlaybackRate;
  onPlaybackRateChange?: (rate: RoundVideoPlaybackRate) => void;
}

function getMediaStateMeta(state: ReaderMediaState) {
  switch (state) {
    case 'completed':
      return {
        label: 'Просмотрено',
        className: 'bg-[rgba(37,151,92,0.12)] text-[rgba(24,113,70,0.88)]',
      };
    case 'in_progress':
      return {
        label: 'Не просмотрено',
        className: 'bg-[rgba(232,93,42,0.12)] text-[rgba(194,77,29,0.92)]',
      };
    case 'not_started':
    default:
      return {
        label: 'Не просмотрено',
        className: 'bg-[rgba(26,26,26,0.06)] text-[rgba(26,26,26,0.52)]',
      };
  }
}

function useCanPlayMimeType(mimeType?: string) {
  return useMemo(() => {
    if (!mimeType || typeof document === 'undefined') {
      return true;
    }

    const probe = document.createElement('video');
    if (typeof probe.canPlayType !== 'function') {
      return true;
    }

    return probe.canPlayType(mimeType) !== '';
  }, [mimeType]);
}

function VideoFailureFallback({
  blockId,
  unsupported,
  onRetry,
}: {
  blockId: string;
  unsupported: boolean;
  onRetry?: () => void;
}) {
  return (
    <div className="px-3 min-[430px]:px-4">
      <div
        data-testid={`video-fallback-${blockId}`}
        className="flex aspect-[4/3] items-center justify-center rounded-[22px] bg-black px-5 text-center shadow-[0_14px_26px_rgba(62,48,23,0.12)]"
      >
        <div className="space-y-2">
          <p className="text-sm font-medium text-white/88">
            {unsupported ? 'Формат видео не поддерживается на этом устройстве' : 'Видео сейчас не удалось загрузить'}
          </p>
          <p className="text-xs leading-relaxed text-white/58">
            {unsupported ? 'Для этого блока нужен совместимый mp4-файл.' : 'Попробуйте открыть другой блок или вернуться к этому видео позже.'}
          </p>
          {!unsupported ? (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex rounded-full bg-white/12 px-3 py-1.5 text-xs font-medium text-white/84"
            >
              Повторить
            </button>
          ) : null}
        </div>
      </div>
      <div className="mt-2">
        <span className="inline-flex rounded-full bg-[rgba(26,26,26,0.06)] px-2.5 py-1 text-[11px] font-medium tracking-[0.01em] text-[rgba(26,26,26,0.52)]">
          Не просмотрено
        </span>
      </div>
    </div>
  );
}

function VideoBlock({
  blockId,
  url,
  mimeType,
  poster,
  style,
  initialPosition,
  mediaState,
  registerMediaElement,
  onPlay,
  onPause,
  onTimeUpdate,
  onEnded,
  playbackRate: controlledPlaybackRate,
  onPlaybackRateChange,
}: VideoBlockProps) {
  const [hasPlaybackError, setHasPlaybackError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRoundOverlayVisible, setIsRoundOverlayVisible] = useState(true);
  const [uncontrolledPlaybackRate, setUncontrolledPlaybackRate] = useState<RoundVideoPlaybackRate>(1);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const roundToggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const roundTogglePointerDownRef = useRef(false);
  const playbackRate = controlledPlaybackRate ?? uncontrolledPlaybackRate;
  const playbackRateRef = useRef<RoundVideoPlaybackRate>(playbackRate);
  const canPlayMimeType = useCanPlayMimeType(mimeType);

  useEffect(() => {
    setHasPlaybackError(false);
    setIsPlaying(false);
    setIsRoundOverlayVisible(true);
  }, [url, mimeType]);

  useEffect(() => {
    playbackRateRef.current = playbackRate;
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const isRound = style === 'round';
  const stateMeta = getMediaStateMeta(mediaState);

  useEffect(() => {
    if (!isRound || !isPlaying || hasPlaybackError) {
      setIsRoundOverlayVisible(true);
      return undefined;
    }

    if (!isRoundOverlayVisible) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsRoundOverlayVisible(false);
    }, ROUND_VIDEO_OVERLAY_AUTO_HIDE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [hasPlaybackError, isPlaying, isRound, isRoundOverlayVisible]);

  const setVideoElement = useCallback((element: HTMLVideoElement | null) => {
    videoRef.current = element;
    if (element) {
      element.playbackRate = playbackRateRef.current;
    }
    registerMediaElement?.(blockId, element);
  }, [blockId, registerMediaElement]);

  const handlePlaybackRateChange = (rate: RoundVideoPlaybackRate) => {
    playbackRateRef.current = rate;
    if (controlledPlaybackRate === undefined) {
      setUncontrolledPlaybackRate(rate);
    }
    onPlaybackRateChange?.(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
  };

  const handleRoundPlaybackToggle = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (isPlaying) {
      video.pause();
      return;
    }

    void video.play()?.catch(() => undefined);
  };

  if (!url) {
    return (
      <div className="mx-3 my-4 flex aspect-[4/3] items-center justify-center rounded-[22px] bg-[rgba(26,26,26,0.08)] text-sm text-[rgba(26,26,26,0.46)] min-[430px]:mx-4">
        Видео недоступно
      </div>
    );
  }

  if (!canPlayMimeType || hasPlaybackError) {
    return (
      <VideoFailureFallback
        blockId={blockId}
        unsupported={!canPlayMimeType}
        onRetry={hasPlaybackError ? () => setHasPlaybackError(false) : undefined}
      />
    );
  }

  return (
    <div className={`px-3 min-[430px]:px-4 ${isRound ? 'flex flex-col items-center' : ''}`}>
      {isRound ? (
        <div
          data-testid={`round-video-media-area-${blockId}`}
          className="relative h-60 w-60"
          onClick={() => setIsRoundOverlayVisible(true)}
        >
          <video
            src={url}
            poster={poster}
            controls
            playsInline
            preload="none"
            ref={setVideoElement}
            className="h-60 w-60 rounded-full object-cover shadow-[0_14px_26px_rgba(62,48,23,0.12)]"
            onError={() => {
              setHasPlaybackError(true);
              setIsPlaying(false);
              setIsRoundOverlayVisible(true);
              onPause?.(blockId);
            }}
            onLoadedMetadata={(e) => {
              const video = e.target as HTMLVideoElement;
              if (mediaState === 'in_progress' && initialPosition && initialPosition > 0) {
                video.currentTime = initialPosition;
              }
              video.playbackRate = playbackRate;
            }}
            onTimeUpdate={(e) => {
              const video = e.target as HTMLVideoElement;
              if (onTimeUpdate && video.duration > 0) {
                onTimeUpdate(blockId, video.currentTime, video.duration);
              }
            }}
            onPlay={() => {
              setIsPlaying(true);
              setIsRoundOverlayVisible(true);
              onPlay?.(blockId);
            }}
            onPause={() => {
              setIsPlaying(false);
              setIsRoundOverlayVisible(true);
              onPause?.(blockId);
            }}
            onEnded={(e) => {
              const video = e.target as HTMLVideoElement;
              setIsPlaying(false);
              setIsRoundOverlayVisible(true);
              onEnded?.(blockId, video.currentTime || video.duration || 0, video.duration || 0);
              onPause?.(blockId);
            }}
          />
          <button
            type="button"
            ref={roundToggleButtonRef}
            data-testid={`round-video-playback-toggle-${blockId}`}
            data-overlay-visible={isRoundOverlayVisible ? 'true' : 'false'}
            aria-label={isPlaying ? 'Поставить круглое видео на паузу' : 'Воспроизвести круглое видео'}
            aria-pressed={isPlaying}
            className={[
              'absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(255,248,231,0.88)] text-[22px] font-semibold leading-none text-[rgba(58,49,39,0.88)] shadow-[0_10px_24px_rgba(40,30,18,0.24)] backdrop-blur transition hover:bg-white focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-[rgba(194,112,46,0.48)]',
              isRoundOverlayVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
            ].join(' ')}
            onPointerDown={() => {
              roundTogglePointerDownRef.current = true;
            }}
            onFocus={() => setIsRoundOverlayVisible(true)}
            onClick={(event) => {
              event.stopPropagation();
              handleRoundPlaybackToggle();
              if (roundTogglePointerDownRef.current) {
                roundToggleButtonRef.current?.blur();
                roundTogglePointerDownRef.current = false;
              }
            }}
          >
            <span aria-hidden="true">{isPlaying ? 'II' : '▶'}</span>
          </button>
        </div>
      ) : (
        <div className="relative aspect-[4/3] overflow-hidden rounded-[22px] bg-black shadow-[0_14px_26px_rgba(62,48,23,0.12)]">
          <video
            src={url}
            poster={poster}
            controls
            playsInline
            preload="none"
            ref={setVideoElement}
            className="absolute inset-0 h-full w-full object-contain"
            onError={() => {
              setHasPlaybackError(true);
              onPause?.(blockId);
            }}
            onLoadedMetadata={(e) => {
              const video = e.target as HTMLVideoElement;
              if (mediaState === 'in_progress' && initialPosition && initialPosition > 0) {
                video.currentTime = initialPosition;
              }
              video.playbackRate = playbackRate;
            }}
            onTimeUpdate={(e) => {
              const video = e.target as HTMLVideoElement;
              if (onTimeUpdate && video.duration > 0) {
                onTimeUpdate(blockId, video.currentTime, video.duration);
              }
            }}
            onPlay={() => onPlay?.(blockId)}
            onPause={() => onPause?.(blockId)}
            onEnded={(e) => {
              const video = e.target as HTMLVideoElement;
              onEnded?.(blockId, video.currentTime || video.duration || 0, video.duration || 0);
              onPause?.(blockId);
            }}
          />
        </div>
      )}
      {isRound ? (
        <div
          data-testid={`playback-speed-${blockId}`}
          className="mt-2 flex w-60 items-center justify-between gap-1 rounded-full bg-[rgba(58,49,39,0.06)] p-1"
          aria-label="Скорость воспроизведения"
          onClick={(event) => event.stopPropagation()}
        >
          {ROUND_VIDEO_PLAYBACK_RATES.map((rate) => (
            <button
              key={rate}
              type="button"
              aria-pressed={playbackRate === rate}
              className={[
                'min-w-0 flex-1 rounded-full px-1.5 py-1 text-[11px] font-semibold tabular-nums transition',
                playbackRate === rate
                  ? 'bg-white text-[rgba(58,49,39,0.86)] shadow-[0_4px_10px_rgba(62,48,23,0.10)]'
                  : 'text-[rgba(58,49,39,0.48)]',
              ].join(' ')}
              onClick={() => handlePlaybackRateChange(rate)}
            >
              {rate}×
            </button>
          ))}
        </div>
      ) : null}
      <div className={`mt-2 ${isRound ? 'w-60' : ''}`}>
        <span
          data-testid={`media-state-${blockId}`}
          className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium tracking-[0.01em] ${stateMeta.className}`}
        >
          {stateMeta.label}
        </span>
      </div>
    </div>
  );
}

interface AudioBlockProps {
  blockId: string;
  url: string;
  title?: string;
  isTelegramVoice?: boolean;
  registerMediaElement?: (blockId: string, element: HTMLMediaElement | null) => void;
  onPlay?: (blockId: string) => void;
  onPause?: (blockId: string) => void;
  onTimeUpdate?: (blockId: string, positionSeconds: number, durationSeconds: number) => void;
  onEnded?: (blockId: string, positionSeconds: number, durationSeconds: number) => void;
}

function AudioBlock({ blockId, url, title, isTelegramVoice = false, registerMediaElement, onPlay, onPause, onTimeUpdate, onEnded }: AudioBlockProps) {
  if (isTelegramVoice && !url) {
    return (
      <div
        data-testid={`voice-audio-unavailable-${blockId}`}
        className="mx-3 rounded-[20px] border border-[rgba(114,86,50,0.14)] bg-[rgba(255,253,248,0.86)] px-4 py-3.5 min-[430px]:mx-4"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(114,86,50,0.58)]">
          Аудиосообщение эксперта
        </p>
        {title ? <p className="mt-1 text-sm font-medium leading-snug text-[rgba(58,49,39,0.82)]">{title}</p> : null}
        <p className="mt-2 text-sm leading-relaxed text-[rgba(26,26,26,0.52)]">
          Аудиосообщение сейчас недоступно
        </p>
      </div>
    );
  }

  if (!url) return null;

  if (isTelegramVoice) {
    return (
      <div
        data-testid={`voice-audio-block-${blockId}`}
        className="mx-3 rounded-[20px] border border-[rgba(114,86,50,0.14)] bg-[rgba(255,253,248,0.9)] px-4 py-3.5 shadow-[0_10px_22px_rgba(62,48,23,0.08)] min-[430px]:mx-4"
      >
        <div className="mb-2.5 flex items-start gap-3">
          <div
            aria-hidden="true"
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgba(194,112,46,0.14)] text-[13px] font-semibold text-[rgba(114,86,50,0.72)]"
          >
            {'▶'}
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(114,86,50,0.58)]">
              Аудиосообщение эксперта
            </p>
            {title ? <p className="mt-1 text-sm font-medium leading-snug text-[rgba(58,49,39,0.84)]">{title}</p> : null}
          </div>
        </div>
        <audio
          src={url}
          controls
          ref={(element) => registerMediaElement?.(blockId, element)}
          className="w-full"
          onTimeUpdate={(e) => {
            const audio = e.target as HTMLAudioElement;
            if (onTimeUpdate && audio.duration > 0) {
              onTimeUpdate(blockId, audio.currentTime, audio.duration);
            }
          }}
          onPlay={() => onPlay?.(blockId)}
          onPause={() => onPause?.(blockId)}
          onEnded={(e) => {
            const audio = e.target as HTMLAudioElement;
            onEnded?.(blockId, audio.currentTime || audio.duration || 0, audio.duration || 0);
            onPause?.(blockId);
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-3 rounded-[22px] bg-[rgba(255,248,231,0.72)] p-3.5 min-[430px]:mx-4">
      {title && <p className="mb-2 text-sm font-medium text-[rgba(26,26,26,0.68)]">{title}</p>}
      <audio
        src={url}
        controls
        ref={(element) => registerMediaElement?.(blockId, element)}
        className="w-full"
        onTimeUpdate={(e) => {
          const audio = e.target as HTMLAudioElement;
          if (onTimeUpdate && audio.duration > 0) {
            onTimeUpdate(blockId, audio.currentTime, audio.duration);
          }
        }}
        onPlay={() => onPlay?.(blockId)}
        onPause={() => onPause?.(blockId)}
        onEnded={(e) => {
          const audio = e.target as HTMLAudioElement;
          onEnded?.(blockId, audio.currentTime || audio.duration || 0, audio.duration || 0);
          onPause?.(blockId);
        }}
      />
    </div>
  );
}

function DividerBlock() {
  return <hr className="mx-5 border-[rgba(26,26,26,0.10)] min-[430px]:mx-6" />;
}

function CalloutBlock({ text, emoji }: { text: string; emoji?: string }) {
  return (
    <div className="mx-4 flex gap-3 rounded-[24px] bg-[rgba(107,78,230,0.08)] p-3.5 min-[430px]:mx-5">
      {emoji && <span className="text-xl shrink-0">{emoji}</span>}
      <p className="text-sm leading-relaxed text-[rgba(26,26,26,0.76)]">{text}</p>
    </div>
  );
}

function ExternalLinkBlock({ url, label }: { url: string; label: string }) {
  // Only https links (per requirements)
  if (!url.startsWith('https://')) return null;

  return (
    <div className="mx-4 min-[430px]:mx-5">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 rounded-[20px] bg-[rgba(255,248,231,0.72)] px-4 py-2.5 text-sm text-[var(--primary)]"
      >
        <span>🔗</span>
        <span>{label}</span>
      </a>
    </div>
  );
}
